import type { JSX } from 'react';
import { Assertions } from 'ts/commons/Assertions';
import { EnumUtils } from 'ts/commons/EnumUtils';
import { JsonStorage } from 'ts/commons/JsonStorage';
import { Links } from 'ts/commons/links/Links';
import { type TaskFilters } from 'ts/commons/links/QualityControlLinks';
import { NavigationHash } from 'ts/commons/NavigationHash';
import { NavigationUtils } from 'ts/commons/NavigationUtils';
import { Icon } from 'ts/components/Icon';
import type { TaskDataContainer } from 'ts/data/TaskDataContainer';
import { FindingsUtils } from 'ts/perspectives/findings/FindingsUtils';
import type { ETaskStatusEntry } from 'typedefs/ETaskStatus';
import { ETaskStatus } from 'typedefs/ETaskStatus';
import type { TrackedFinding } from 'typedefs/TrackedFinding';

/** Mapping from task statuses to icons components. */
export const TASK_STATUS_ICONS: Record<ETaskStatusEntry, JSX.Element> = {
	OPEN: <Icon name="list" color="red" style={{ paddingLeft: '0', float: 'left' }} />,
	RESOLVED: <Icon name="hourglass outline" color="yellow" style={{ float: 'left' }} />,
	VERIFIED: <Icon name="tasks" color="green" style={{ float: 'left' }} />,
	DISCARDED: <Icon name="times circle outline" color="grey" style={{ float: 'left' }} />
};

const VALID_STATUS_VALUES = EnumUtils.getValues(ETaskStatus.values);

/** Mapping from task statuses to readable names. */
export const TASK_STATUS_TOOLTIPS: Record<ETaskStatusEntry, string> = {
	OPEN: 'Open',
	RESOLVED: 'Ready to review',
	VERIFIED: 'Closed',
	DISCARDED: 'Discarded'
};

/** Utility functions related to tasks. */
export class TaskUtils {
	/** The statuses parameter. */
	public static readonly STATUSES_PARAMETER = 'statuses';

	/** The tags parameter. */
	public static readonly TAGS_PARAMETER = 'tags';

	/** The name of the query parameter used to store the assignee filter in the navigation hash. */
	public static readonly ASSIGNEE_FILTER_PARAMETER = 'assignee';

	/** The name of the query parameter used to store the author filter in the navigation hash. */
	public static readonly AUTHOR_FILTER_PARAMETER = 'author';

	/**
	 * The session storage key that is used to transport finding and code snippet data to another tab for creating a new
	 * task based on it.
	 */
	private static readonly FINDINGS_FOR_TASK_SESSION_STORAGE_KEY = 'new-task';

	/**
	 * Navigates to the edit view of the given task in the specified project.
	 *
	 * @param project The project to which the task belongs.
	 * @param taskId ID of the task. Can be null to create a new task.
	 * @param keepTaskFilters Whether to keep the current task filter stored in the hash. Defaults to false
	 */
	public static navigateToTaskEditView(project: string, taskId: number | null, keepTaskFilters = false): void {
		TaskUtils.navigateToTaskView(true, project, taskId, keepTaskFilters);
	}

	/**
	 * Navigates to the task detail view of the given task in the specified project.
	 *
	 * @param project The project to which the task belongs.
	 * @param taskId ID of the task. Can be null to create a new task.
	 * @param keepTaskFilters Whether to keep the current task filter stored in the hash. Defaults to false.
	 * @param openInNewTab Whether to open the task details in a new tab. Defaults to false.
	 */
	public static navigateToTaskDetailView(
		project: string,
		taskId: number,
		keepTaskFilters = false,
		openInNewTab = false,
		replace = false
	): void {
		TaskUtils.navigateToTaskView(false, project, taskId, keepTaskFilters, openInNewTab, replace);
	}

	/** Navigates to the specified view of the given task in the specified project */
	private static navigateToTaskView(
		edit: boolean,
		project: string,
		taskId: number | null,
		keepTaskFilters = false,
		openInNewTab = false,
		replace = false
	): void {
		let taskFilters: TaskFilters | undefined;
		if (keepTaskFilters) {
			taskFilters = this.getTaskFilters(NavigationHash.getCurrent());
		}
		let link: string;
		if (edit) {
			link = Links.editTask(project, taskId, taskFilters);
		} else {
			link = Links.taskDetails(project, taskId!, taskFilters);
		}
		if (openInNewTab) {
			NavigationUtils.openInNewTab(link, true);
		} else {
			NavigationUtils.updateLocation(link, replace);
		}
	}

	/**
	 * Returns the branch name that should be used for storing the provided finding in a task. Usually this resolves to
	 * the provided branch (retrieved from current selected branch). Only exception is if the finding is a spec item
	 * finding, which only exists on its own specific branch.
	 */
	public static getFindingBranchName(finding: TrackedFinding, branch?: string | null): string | undefined {
		if (FindingsUtils.isSpecItemFindingLocation(finding.location)) {
			// Keep the branch of spec item findings and don't overwrite with selected branch
			return finding.birth.branchName;
		}
		return branch ?? undefined;
	}

	/** Gets the task data stored in the browser's session store. */
	public static getTaskDataFromSessionStore(): TaskDataContainer | null {
		const sessionStorage = JsonStorage.SESSION_STORAGE;
		return sessionStorage.get(TaskUtils.FINDINGS_FOR_TASK_SESSION_STORAGE_KEY);
	}

	/**
	 * Stores the given task data in the browser's session store.
	 *
	 * @param task The task data to store
	 */
	public static putTaskDataInSessionStore(task: TaskDataContainer): void {
		const sessionStorage = JsonStorage.SESSION_STORAGE;
		sessionStorage.set(TaskUtils.FINDINGS_FOR_TASK_SESSION_STORAGE_KEY, task);
	}

	/** Returns the task filters */
	public static getTaskFilters(hash: NavigationHash): TaskFilters {
		const statuses = hash
			.getArray(TaskUtils.STATUSES_PARAMETER)
			?.filter(statusName => VALID_STATUS_VALUES.includes(statusName as ETaskStatusEntry))
			.map(statusName => ETaskStatus[statusName as ETaskStatusEntry]) ?? [ETaskStatus.OPEN];
		const tags = hash.getArray(TaskUtils.TAGS_PARAMETER, []);
		const assignees = hash.getArray(TaskUtils.ASSIGNEE_FILTER_PARAMETER, []);
		const authors = hash.getArray(TaskUtils.AUTHOR_FILTER_PARAMETER, []);
		return { statuses, assignees, authors, tags };
	}

	/**
	 * Returns the possible task statuses for the given task and current user with a developer role. This always
	 * includes the current status of the task.
	 */
	public static getPossibleStatusesForDev(status: ETaskStatusEntry): ETaskStatus[] {
		switch (status) {
			case ETaskStatus.OPEN.name:
				return [ETaskStatus.OPEN, ETaskStatus.RESOLVED];
			case ETaskStatus.RESOLVED.name:
				return [ETaskStatus.RESOLVED, ETaskStatus.OPEN];
			case ETaskStatus.DISCARDED.name:
				return [ETaskStatus.DISCARDED];
			case ETaskStatus.VERIFIED.name:
				return [ETaskStatus.VERIFIED];
			default:
				Assertions.fail('Unknown task status: ' + status);
		}
	}

	/**
	 * Returns the possible task statuses for the given task and current user with a TQE role (may edit tasks). This
	 * always includes the current status of the task.
	 */
	public static getPossibleStatusesForTqe(status: ETaskStatusEntry): ETaskStatus[] {
		switch (status) {
			case ETaskStatus.OPEN.name:
				return [ETaskStatus.OPEN, ETaskStatus.RESOLVED, ETaskStatus.DISCARDED];
			case ETaskStatus.RESOLVED.name:
				return [ETaskStatus.RESOLVED, ETaskStatus.VERIFIED, ETaskStatus.DISCARDED, ETaskStatus.OPEN];
			case ETaskStatus.DISCARDED.name:
				return [ETaskStatus.DISCARDED, ETaskStatus.OPEN];
			case ETaskStatus.VERIFIED.name:
				return [ETaskStatus.VERIFIED, ETaskStatus.OPEN];
			default:
				Assertions.fail('Unknown task status: ' + status);
		}
	}
}
